import { Box, Button, Grid, Typography } from "@mui/material";
import { red } from "@mui/material/colors";
import { BrandsContext } from "contexts/Brands";
import { useContext, useState } from "react";
import { BentoBrand, BentoBrandTier } from "schemas/dashboard";

import ExclusiveBrand from "components/ExclusiveBrand";
import { EXCLUSIVE_CONTACTED_THRESHOLD } from "constants/influencer";
import { BrandCardSource } from "features/Influencer/Discover/Results/schema";
import { usePageSize } from "utils/usePageSize";

import BrandAnnotation from "../BrandAnnotation";
import { NonVaBrandAnnotations } from "../BrandAnnotation/schema";
import BrandLinks from "../BrandLinks/BrandLinks";
import BrandLocations from "../BrandLocations";
import BrandSimilar from "../BrandSimilar";
import PartnershipData from "../PartnershipData";
import SimilarBrandSkeletonBody from "./SimilarBrandSkeletonBody";
import SkeletonBody from "./SkeletonBody";
import styles from "./styles";

interface Props {
  selectedBrand: BentoBrand | null;
  fetchLoading: boolean;
  similarBrands: BentoBrand[];
  fetchSimilarLoading: boolean;
  isPopupView?: boolean;
  source?: BrandCardSource;
  mobileOverride?: boolean;
}

const BrandBody = ({
  selectedBrand,
  fetchLoading,
  similarBrands,
  fetchSimilarLoading,
  isPopupView,
  source,
  mobileOverride,
}: Props) => {
  const [imgError, setImgError] = useState<boolean>(false);
  const { setOpenReportInfoDialog } = useContext(BrandsContext);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { isRecommendedPage } = usePageSize();

  const renderReportButton = () => {
    return (
      selectedBrand?.id && (
        <Button
          sx={{
            fontSize: 9,
            color: red[300],
            textTransform: "none",
            mt: { xs: 1, md: 0 },
            ml: -1,
          }}
          onClick={() => {
            setOpenReportInfoDialog(selectedBrand);
          }}
        >
          Report Wrong Information
        </Button>
      )
    );
  };

  const displayExclusiveBrand =
    !isRecommendedPage &&
    isPopupView &&
    (selectedBrand?.tier === BentoBrandTier.tier_one ||
      (selectedBrand?.contactedRecentlyAmount &&
        selectedBrand?.contactedRecentlyAmount >=
          EXCLUSIVE_CONTACTED_THRESHOLD));

  return (
    <Box>
      {fetchLoading ? (
        <SkeletonBody
          isPopupView={isPopupView}
          isOnboarding={
            source === BrandCardSource.ONBOARDING_BRAND ||
            source === BrandCardSource.CAMPAIGN_BRAND
          }
        />
      ) : (
        <>
          {isPopupView && !!selectedBrand?.mainImageSource && (
            <Box
              component="img"
              src={selectedBrand?.mainImageUrl}
              sx={styles.mainImage}
            />
          )}
          {isPopupView && !!selectedBrand?.logoUrl && !imgError && (
            <Box display="flex" justifyContent="center">
              <Box
                sx={[
                  styles.logoWrapper,
                  {
                    mt: !!selectedBrand?.mainImageSource ? "-50px" : 2,
                  },
                ]}
              >
                <Box
                  component="img"
                  src={selectedBrand?.logoUrl}
                  sx={styles.logo}
                  onError={() => setImgError(true)}
                />
              </Box>
            </Box>
          )}
          {displayExclusiveBrand && (
            <Box display="flex" justifyContent="center">
              <ExclusiveBrand
                anchorEl={anchorEl}
                setAnchorEl={setAnchorEl}
                selectedBrand={selectedBrand}
              />
            </Box>
          )}
          <Box
            sx={
              isRecommendedPage
                ? styles.recommendationPage
                : isPopupView
                  ? styles.popUpBody
                  : styles.body
            }
          >
            {isPopupView && (
              <>
                <Typography sx={{ textAlign: "center" }} variant="h3">
                  {selectedBrand?.normalizedName}{" "}
                </Typography>
                {selectedBrand && <Box>{selectedBrand?.aboutSummary}</Box>}
                {renderReportButton()}
              </>
            )}

            <Grid
              container
              sx={isPopupView ? styles.section : {}}
              spacing={isPopupView ? 0 : 3}
            >
              {source !== BrandCardSource.ONBOARDING_BRAND && selectedBrand && (
                <Grid
                  item
                  xs={12}
                  sm={isPopupView ? 12 : 6}
                  md={isPopupView ? 12 : mobileOverride ? 6 : 4}
                  sx={isPopupView ? styles.section : {}}
                >
                  <PartnershipData bentoBrand={selectedBrand} />
                </Grid>
              )}

              <Grid
                item
                xs={12}
                sm={
                  isPopupView || source === BrandCardSource.ONBOARDING_BRAND
                    ? 12
                    : 6
                }
                md={
                  isPopupView || source === BrandCardSource.ONBOARDING_BRAND
                    ? 12
                    : mobileOverride
                      ? 6
                      : 4
                }
              >
                {selectedBrand && (
                  <BrandLocations selectedBrand={selectedBrand} />
                )}

                {NonVaBrandAnnotations?.map((header, index) => (
                  <BrandAnnotation
                    header={header}
                    selectedBrand={selectedBrand}
                    key={index}
                  />
                ))}
              </Grid>

              {selectedBrand && (
                <Grid
                  item
                  xs={12}
                  sm={
                    isPopupView || source === BrandCardSource.ONBOARDING_BRAND
                      ? 12
                      : 6
                  }
                  md={
                    isPopupView || source === BrandCardSource.ONBOARDING_BRAND
                      ? 12
                      : mobileOverride
                        ? 6
                        : 4
                  }
                  sx={isPopupView ? styles.section : {}}
                >
                  <Typography>
                    <strong>Social Media Links</strong>
                  </Typography>
                  <BrandLinks bentoBrand={selectedBrand} />
                </Grid>
              )}
            </Grid>

            {source !== BrandCardSource.ONBOARDING_BRAND &&
              source !== BrandCardSource.CAMPAIGN_BRAND &&
              source !== BrandCardSource.MORE_INFO_BRAND &&
              !fetchSimilarLoading &&
              selectedBrand &&
              similarBrands?.length > 0 && (
                <Grid container sx={styles.section}>
                  <BrandSimilar
                    similarBrands={similarBrands}
                    bentoBrand={selectedBrand}
                    isPopupView={isPopupView}
                  />
                </Grid>
              )}
          </Box>
        </>
      )}
      {source !== BrandCardSource.ONBOARDING_BRAND &&
        source !== BrandCardSource.CAMPAIGN_BRAND &&
        fetchSimilarLoading &&
        !isPopupView && <SimilarBrandSkeletonBody />}
    </Box>
  );
};

export default BrandBody;
